import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Settings, UserCog, Lock, Bell, ChevronRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { ErpFieldLabel } from '../../shared/utils/erpFormFieldClasses';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { setPortalHijriPref, getPortalHijriPref } from './employeePortalShared';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'accountPage';

export default function EmployeePortalAccountPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.employee_portal.pages.${NS}.${key}`, opts);
  const navigate = useNavigate();
  const qawaem = isSaudiBrand();
  const { name, email, currentUser } = usePortalEmployeeContext();
  const displayUser = currentUser?.username || name || '—';
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem('employee_portal_prefs');
      if (raw) {
        return {
          emailNotifications: '1',
          language: 'ar',
          theme: 'dark',
          showHijriDates: getPortalHijriPref(),
          ...JSON.parse(raw),
        };
      }
    } catch (_) { /* ignore */ }
    return { emailNotifications: '1', language: 'ar', theme: 'dark', showHijriDates: false };
  });

  useEffect(() => {
    try {
      localStorage.setItem('employee_portal_prefs', JSON.stringify(prefs));
      setPortalHijriPref(!!prefs.showHijriDates);
      document.body.dataset.portalPrefsSaved = '1';
    } catch (_) { /* ignore */ }
  }, [prefs]);

  const links = [
    { labelKey: 'link_profile', path: '/employee-portal/profile', icon: UserCog, internal: true },
    { labelKey: 'link_global_profile', path: '/profile', icon: ExternalLink, internal: true },
    { labelKey: 'link_password', path: '/settings', icon: Lock, internal: true },
  ];

  const langOpts = [{ value: 'ar', label: p('lang_ar') }];

  const notifOpts = [
    { value: '1', label: p('notif_on') },
    { value: '0', label: p('notif_off') },
  ];

  const hijriOpts = [
    { value: '1', label: p('hijri_on') },
    { value: '0', label: p('hijri_off') },
  ];

  const handleSavePrefs = (e) => {
    e?.preventDefault?.();
    try {
      localStorage.setItem('employee_portal_prefs', JSON.stringify(prefs));
      setPortalHijriPref(!!prefs.showHijriDates);
      document.body.dataset.portalPrefsSaved = '1';
      notifyUpdated(showNotify, p('section_preferences'));
    } catch (_) {
      notifyError(showNotify, p('prefs_error'));
    }
  };

  return (
    <>
    <div className="space-y-6 employees-container">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-700">
            <Settings className="h-8 w-8 text-white" />
          </div>
          {p('title')}
        </h1>
        <p className="text-white/60 mt-2">{p('subtitle')}</p>
      </div>

      {qawaem ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QawaemStatsCard label={p('stat_user')} value={displayUser} icon={UserCog} variant="copper" data-testid="account-stat-user" />
          <QawaemStatsCard label={p('stat_email')} value={email || '—'} icon={Bell} variant="forest" />
          <QawaemStatsCard label={p('stat_role')} value={currentUser?.role || p('role_employee')} icon={Settings} variant="sage" />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">{p('section_links')}</h2>
          <div className="space-y-2">
            {links.map((link) => (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white text-left"
              >
                <span className="flex items-center gap-2">
                  <link.icon size={18} className="text-teal-400" />
                  {p(link.labelKey)}
                </span>
                <ChevronRight size={16} className="text-white/40" />
              </button>
            ))}
          </div>
          <p className="text-white/40 text-sm mt-4">
            {p('profile_hint')}{' '}
            <Link to="/employee-portal/profile" className="text-teal-400 hover:underline">{p('link_profile')}</Link>
          </p>
        </div>

        <form onSubmit={handleSavePrefs} className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white mb-2">{p('section_preferences')}</h2>
          <div>
            <ErpFieldLabel label={p('field_notifications')} className="block w-full text-white/70 text-sm mb-1 font-medium text-right" />
            <CustomSelect
              value={prefs.emailNotifications}
              onChange={(v) => setPrefs((prev) => ({ ...prev, emailNotifications: v }))}
              options={notifOpts}
            />
          </div>
          <div>
            <ErpFieldLabel label={p('field_language')} className="block w-full text-white/70 text-sm mb-1 font-medium text-right" />
            <CustomSelect
              value="ar"
              onChange={() => {}}
              options={langOpts}
            />
          </div>
          <div>
            <ErpFieldLabel label={p('field_hijri_dates')} className="block w-full text-white/70 text-sm mb-1 font-medium text-right" />
            <CustomSelect
              value={prefs.showHijriDates ? '1' : '0'}
              onChange={(v) => setPrefs((prev) => ({ ...prev, showHijriDates: v === '1' }))}
              options={hijriOpts}
            />
          </div>
          <button type="submit" data-testid="account-save-prefs" className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-700 text-white rounded-xl font-semibold">
            {p('save_prefs')}
          </button>
        </form>
      </div>
    </div>

    <AppNotificationToast
      visible={notifyToast.visible}
      message={notifyToast.message}
      messageHtml={notifyToast.messageHtml}
      variant={notifyToast.variant}
      status={notifyToast.status}
      showActions={notifyToast.showActions}
      topLabel={notifyToast.topLabel}
      onDismiss={dismissNotify}
      placement="bottom"
    />
    </>
  );
}
